/**
 * <s-product-image-zoom> — PDP image zoom preloader and close-up viewer.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component warms the selected zoom image on hover/focus, and inside the
 * fullscreen dialog a click/tap toggles a 2.5x close-up. The close-up enlarges the
 * image inside a scrollable stage, so trackpad scrolling and touch dragging pan
 * natively, and mouse movement also pans.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog [data-zoom-stage] > img[data-zoom-image] — high-resolution image inside a stage.
 */
const ZOOM_SCALE = 2.5;

class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleZoomClick, opts);
    this.addEventListener('keydown', this.#handleZoomKey, opts);
    this.addEventListener('pointermove', this.#handleZoomMove, opts);
    // dialog close does not bubble, so listen in the capture phase.
    this.addEventListener('close', this.#handleDialogClose, { signal: this.#ac.signal, capture: true });

    for (const image of this.querySelectorAll('dialog img[data-zoom-image]')) {
      image.style.cursor = 'zoom-in';
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #zoomImageFrom(target) {
    if (!(target instanceof Element)) return null;
    const image = target.closest('dialog img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement) || !this.contains(image)) return null;
    return image;
  }

  #handleZoomClick = (event) => {
    const image = this.#zoomImageFrom(event.target);
    if (!image) return;
    event.preventDefault();
    event.stopPropagation();

    if (image.hasAttribute('data-zoomed')) {
      this.#zoomOut(image);
    } else {
      this.#zoomIn(image, event.clientX, event.clientY);
    }
  };

  #handleZoomKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const image = this.#zoomImageFrom(event.target);
    if (!image) return;
    event.preventDefault();

    if (image.hasAttribute('data-zoomed')) {
      this.#zoomOut(image);
    } else {
      const rect = image.getBoundingClientRect();
      this.#zoomIn(image, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
  };

  #handleZoomMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    const image = this.#zoomImageFrom(event.target);
    if (!image || !image.hasAttribute('data-zoomed')) return;
    const stage = image.parentElement;
    if (!stage) return;

    const rect = stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const fx = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const fy = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    stage.scrollLeft = fx * (stage.scrollWidth - stage.clientWidth);
    stage.scrollTop = fy * (stage.scrollHeight - stage.clientHeight);
  };

  #handleDialogClose = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    const image = event.target.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement && image.hasAttribute('data-zoomed')) this.#zoomOut(image);
  };

  #zoomIn(image, clientX, clientY) {
    const stage = image.parentElement;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const fx = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const fy = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));

    image.sizes = `${Math.round(ZOOM_SCALE * 100)}vw`;
    image.style.width = `${rect.width * ZOOM_SCALE}px`;
    image.style.height = `${rect.height * ZOOM_SCALE}px`;
    image.style.maxWidth = 'none';
    image.style.maxHeight = 'none';
    image.style.cursor = 'zoom-out';
    stage.style.overflow = 'auto';
    image.setAttribute('data-zoomed', '');
    image.setAttribute('aria-pressed', 'true');

    stage.scrollLeft = fx * (stage.scrollWidth - stage.clientWidth);
    stage.scrollTop = fy * (stage.scrollHeight - stage.clientHeight);
  }

  #zoomOut(image) {
    const stage = image.parentElement;
    image.removeAttribute('data-zoomed');
    image.setAttribute('aria-pressed', 'false');
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.maxWidth = '';
    image.style.maxHeight = '';
    image.style.cursor = 'zoom-in';
    image.sizes = '100vw';
    if (stage) {
      stage.scrollLeft = 0;
      stage.scrollTop = 0;
      stage.style.overflow = '';
    }
  }

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
